import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import toast from "react-hot-toast";
import { BsArrowUpRight } from "react-icons/bs";
import ArrowLeftIcon from "assets/svg/arrow-left-icon";
import Layout from "layouts/layout";
import { DetailedViewCard } from "components/profile/milestones/detailed-view";
import { ListViewCard } from "components/profile/milestones/list-view";
import { DetailedViewCard as ColumnViewCard } from "components/profile/milestones/detailed-third-view";
import GroupButton from "components/button/group-btn";
import { useDialogHook } from "utils/customhooks";
import WatchMediaModal from "components/profile/milestones/media-button/watch";
import ImageGalleryModal from "components/profile/milestones/media-button/image-gallery";
import PdfViewerModal from "components/profile/milestones/media-button/pdf-viewer";
import ViewMilestoneDialog from "components/profile/milestones/view-dialog";
import PopInfo from "components/info-popup/pop-info";
import { formatDate, randomExtension, currentLang } from "utils/utils";
import LoadingIndicator from "assets/svg/loading-indicator";
import ProfileDefault from "assets/images/profile_default.png";

const PUBLIC_DOMAIN = process.env.REACT_APP_PUBLIC_DOMAIN;
const LIMIT = 10;

/**
 * Milestones Feed — an aggregated, newest-first stream of milestones from every
 * profile the current user follows. Each row reuses the profile page's
 * DetailedViewCard (image left, details right) under an author header, with a
 * category filter and a people/milestone search in the page header. Data comes
 * from GET public/milestones/feed, which enforces privacy server-side.
 */
function MilestonesFeed({ userData }) {
  const { t } = useTranslation();
  const { http } = global.services;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialised, setInitialised] = useState(false);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [view, setView] = useState("detailed");

  const contentRef = useRef(null);
  const fetchingRef = useRef(false);
  const skipRef = useRef(0);
  const loadMoreRef = useRef(true);

  const watchMediaModal = useDialogHook(WatchMediaModal);
  const imageGalleryModal = useDialogHook(ImageGalleryModal);
  const pdfViewerModal = useDialogHook(PdfViewerModal);
  const modalViewMilestone = useDialogHook(ViewMilestoneDialog);

  // Media viewers — same wrappers the profile page uses, so the card's media
  // buttons behave identically here.
  const handleWatch = (title, url, orientation = "Landscape", tiktok = false, header = "") => {
    watchMediaModal({ title, url, tiktok, header, orientation }, () => {});
  };
  const handleImageGallery = (title, button, media) => {
    imageGalleryModal({ title, button, media }, () => {});
  };
  const handleViewPdf = (title, url, media, allData) => {
    const medMeta = media?.[0] || {};
    pdfViewerModal({ title, url, medMeta, allData }, () => {});
  };

  const ownerProfilePath = (m) => {
    const uname = m?.profile?.user?.username || m?.profile?.username || "profile";
    return `/${uname}-${randomExtension()}/profile/${m?.profile?.id}`;
  };

  const authorName = (m) => {
    const owner = m?.profile;
    const full = `${owner?.user?.first_name || ""} ${owner?.user?.last_name || ""}`.trim();
    return owner?.profile_name || full || owner?.user?.username || "";
  };

  // A milestone whose start date is in the future reads as "upcoming".
  const isUpcoming = (m) => {
    if (!m?.start_year) return false;
    const dt = moment({
      year: Number(m.start_year),
      month: m.start_month ? Number(m.start_month) - 1 : 0,
      day: m.start_day ? Number(m.start_day) : 1,
    });
    return dt.isValid() && dt.isAfter(moment(), "day");
  };

  const copyLink = (m) => {
    const url = `${PUBLIC_DOMAIN}${ownerProfilePath(m)}?milestone=${m?.hash_id}`;
    navigator.clipboard.writeText(url).then(() => toast.success(t("linkCopied")));
  };

  // Open the milestone in a popup on the feed itself (instead of navigating to
  // the owner's profile). Mirrors the profile's list/column "view" dialog.
  const viewMilestone = (m) => {
    modalViewMilestone(
      {
        profile: m?.profile,
        isYourProfile: false,
        data: m,
        onWatchMedia: handleWatch,
        onViewImage: handleImageGallery,
        onEdit: () => {},
        onDelete: () => {},
        onReport: () => {},
        handleViewPdf: handleViewPdf,
        onShare: () => {},
        onCopyLink: () => copyLink(m),
        share: false,
      },
      () => {}
    );
  };

  const fetchFeed = useCallback(async () => {
    if (fetchingRef.current || !loadMoreRef.current || !userData?.profile?.id) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const { data } = await http.get("public/milestones/feed", {
        skip: skipRef.current,
        limit: LIMIT,
        user_profile_id: userData?.profile?.id,
      });
      const rows = data?.data || [];
      setItems((prev) => [...prev, ...rows]);
      skipRef.current += rows.length;
      loadMoreRef.current = rows.length > 0;
    } catch (e) {
      console.log("error@fetchFeed", e);
    } finally {
      setLoading(false);
      setInitialised(true);
      fetchingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.profile?.id]);

  useEffect(() => {
    // reset when the active profile changes
    setItems([]);
    skipRef.current = 0;
    loadMoreRef.current = true;
    setInitialised(false);
    if (userData?.profile?.id) fetchFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.profile?.id]);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el || fetchingRef.current || !loadMoreRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - scrollHeight / 3) {
      fetchFeed();
    }
  };

  // Category options are built from whatever has loaded so far.
  const categoryOptions = useMemo(() => {
    const names = new Set();
    items.forEach((m) =>
      (m.categories || []).forEach((c) => {
        const name = c?.[currentLang()] || c?.name;
        if (name) names.add(name);
      })
    );
    return Array.from(names).sort();
  }, [items]);

  // Client-side filter over the loaded rows (category + people/milestone search).
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((m) => {
      if (category) {
        const names = (m.categories || []).map((c) => c?.[currentLang()] || c?.name);
        if (!names.includes(category)) return false;
      }
      if (q) {
        const hay = `${authorName(m)} ${m.title || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, category, search]);

  return (
    <Layout
      title={t("milestonesFeed", "Milestones Feed")}
      handleScroll={handleScroll}
      contentRef={contentRef}>
      <div className="auth-page-wrapper pb-8.5">
        <div className="w-full container-max-width pt-9 pb-10">
          {/* back to profile (Marker.io: add a back-to-profile link on top of the feed) */}
          {userData?.user?.username && userData?.profile?.id && (
            <Link to={`/${userData.user.username}/profile/${userData.profile.id}`}>
              {/* Match the Settings page "Back to Profile" styling (same weight). */}
              <div className="flex items-center font-bold gap-1 hover:text-lightPrimary mb-2 text-sm text-darkerGray">
                <ArrowLeftIcon className="font-bold" />
                <span>{t("backToProfile", "Back to Profile")}</span>
              </div>
            </Link>
          )}
          {/* header: title */}
          <div className="flex items-center gap-2 mb-5">
            <h1 className="text-3xl text-lightBlack tracking-tighter font-bold">
              {t("milestonesFeed", "Milestones Feed")}
            </h1>
            <PopInfo containerClass="relative" type="milestones_feed" />
          </div>
          {/* filters (left) + view switcher (right), on one row */}
          <div className="w-full flex flex-col 3sm:flex-row 3sm:items-center justify-between gap-3 pb-5 mb-7 border-b border-gray">
            <div className="flex flex-col 3sm:flex-row items-stretch 3sm:items-center gap-3">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="appearance-none h-11 w-full 3sm:w-60 rounded-md border border-gray bg-white bg-no-repeat pl-3.5 pr-10 text-sm text-lightBlack focus:outline-none"
                style={{
                  backgroundImage:
                    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23555555' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
                  backgroundPosition: "right 0.85rem center",
                  backgroundSize: "14px",
                }}>
                <option value="">{t("allCategories", "All categories")}</option>
                {categoryOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("searchPeopleOrMilestones", "Search people or milestones")}
                className="appearance-none h-11 w-full 3sm:w-72 rounded-md border border-gray bg-white px-3.5 py-0 text-sm text-lightBlack placeholder:text-darkerGray focus:outline-none"
              />
            </div>
            <div className="flex justify-start 3sm:justify-end">
              <GroupButton active={view} onClick={(value) => setView(value)} />
            </div>
          </div>

          <div className={view === "column" ? "grid grid-cols-1 2sm:grid-cols-2 gap-6" : ""}>
          {filtered.map((m, index) => {
            const owner = m?.profile;
            const avatar =
              owner?.profile_image_webp_url || owner?.profile_image_url || ProfileDefault;
            const thumbnail = m?.thumbnail ? m.thumbnail.webp_url || m.thumbnail.url : null;
            const date = formatDate(m, owner?.dateFormat, t);
            const path = ownerProfilePath(m);
            const openPath = `${path}?milestone=${encodeURIComponent(m?.hash_id || "")}`;

            // "column" view — the profile's compact stacked card, in a 2-up grid
            if (view === "column") {
              return (
                <div key={`${m.id}-${index}`} className="w-full">
                  {/* author header */}
                  <div className="w-full flex items-center justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <Link to={path} className="shrink-0">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-black/10 flex items-center justify-center">
                          <img
                            src={avatar}
                            alt="profile"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = ProfileDefault;
                            }}
                          />
                        </div>
                      </Link>
                      <div className="text-xs leading-4 min-w-0 truncate">
                        <Link
                          to={path}
                          className="font-bold text-lightBlack hover:text-lightPrimary">
                          {authorName(m)}
                        </Link>
                        <span className="text-darkerGray">
                          {" "}
                          {isUpcoming(m)
                            ? t("addedAnUpcomingMilestone", "added an upcoming milestone")
                            : t("addedAMilestone", "added a milestone")}
                        </span>
                        {m?.created_at && (
                          <span className="text-darkerGray"> · {moment(m.created_at).fromNow()}</span>
                        )}
                      </div>
                    </div>
                    <Link
                      to={openPath}
                      title={t("view", "View")}
                      className="shrink-0 bg-thBg hover:bg-gray w-8 h-8 rounded-md flex items-center justify-center text-lightBlack">
                      <BsArrowUpRight />
                    </Link>
                  </div>
                  <ColumnViewCard
                    dataId={m.id}
                    profile={owner}
                    featured={m}
                    title={m.title}
                    dateNote={m.date_note}
                    date={date}
                    people={m.people}
                    positions={m.positions}
                    platform={m.platform}
                    location={m.location}
                    description={m.description}
                    url={thumbnail}
                    viewUrl={thumbnail}
                    privacy={m.privacy}
                    isAdultContent={m.is_adult_content ? true : false}
                    categories={m.categories}
                    buttons={m.media_type}
                    mention_names={m.mention_names}
                    onWatchMedia={handleWatch}
                    onViewImage={handleImageGallery}
                    handleViewPdf={handleViewPdf}
                    onCopyLink={() => copyLink(m)}
                    onShare={() => {}}
                    share={false}
                    isYourProfile={false}
                  />
                </div>
              );
            }

            return (
              <div
                key={`${m.id}-${index}`}
                className="w-full mt-6 first:mt-0">
                {/* author header */}
                <div className="w-full flex items-center justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <Link to={path} className="shrink-0">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-black/10 flex items-center justify-center">
                        <img
                          src={avatar}
                          alt="profile"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = ProfileDefault;
                          }}
                        />
                      </div>
                    </Link>
                    <div className="text-sm leading-5 min-w-0">
                      <Link
                        to={path}
                        className="font-bold text-lightBlack hover:text-lightPrimary">
                        {authorName(m)}
                      </Link>
                      <span className="text-darkerGray">
                        {" "}
                        {isUpcoming(m)
                          ? t("addedAnUpcomingMilestone", "added an upcoming milestone")
                          : t("addedAMilestone", "added a milestone")}
                      </span>
                      {m?.created_at && (
                        <span className="text-darkerGray"> · {moment(m.created_at).fromNow()}</span>
                      )}
                    </div>
                  </div>
                  <Link
                    to={openPath}
                    title={t("view", "View")}
                    className="shrink-0 bg-thBg hover:bg-gray w-9 h-9 rounded-md flex items-center justify-center text-lightBlack">
                    <BsArrowUpRight />
                  </Link>
                </div>

                {view === "list" ? (
                  <ListViewCard
                    dataId={m.id}
                    profile={owner}
                    featured={m}
                    title={m.title}
                    dateNote={m.date_note}
                    date={date}
                    people={m.people}
                    positions={m.positions}
                    platform={m.platform}
                    location={m.location}
                    url={thumbnail}
                    viewUrl={thumbnail}
                    privacy={m.privacy}
                    isAdultContent={m.is_adult_content ? true : false}
                    categories={m.categories}
                    mention_names={m.mention_names}
                    onView={() => viewMilestone(m)}
                    onCopyLink={() => copyLink(m)}
                    onShare={() => {}}
                    share={false}
                    isYourProfile={false}
                  />
                ) : (
                  <DetailedViewCard
                  dataId={m.id}
                  profile={owner}
                  featured={m}
                  details={m}
                  url={thumbnail}
                  viewUrl={thumbnail}
                  title={m.title}
                  dateNote={m.date_note}
                  date={date}
                  people={m.people}
                  positions={m.positions}
                  platform={m.platform}
                  location={m.location}
                  description={m.description}
                  privacy={m.privacy}
                  isAdultContent={m.is_adult_content ? true : false}
                  categories={m.categories}
                  buttons={m.media_type}
                  mention_names={m.mention_names}
                  onWatchMedia={handleWatch}
                  onViewImage={handleImageGallery}
                  handleViewPdf={handleViewPdf}
                  onCopyLink={() => copyLink(m)}
                  share={false}
                  isYourProfile={false}
                  permission={null}
                  showItemList={false}
                  mt=""
                  userData={userData}
                />
                )}
              </div>
            );
          })}
          </div>

          {loading && (
            <div className="w-full flex justify-center py-8">
              <LoadingIndicator className="w-6 h-6 text-primary" />
            </div>
          )}

          {initialised && !loading && items.length === 0 && (
            <div className="w-full text-center py-16">
              <p className="text-lg font-semibold text-lightBlack mb-1">
                {t("feedEmptyTitle", "Your feed is quiet")}
              </p>
              <p className="text-sm text-darkerGray">
                {t(
                  "feedEmptyBody",
                  "Follow people to see their milestones here as they post them."
                )}
              </p>
            </div>
          )}

          {initialised && !loading && items.length > 0 && filtered.length === 0 && (
            <div className="w-full text-center py-16">
              <p className="text-sm text-darkerGray">
                {t("feedNoMatches", "No milestones match your filters.")}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

const mapStateToProps = (state) => ({
  userData: state.user.userData,
});

export default connect(mapStateToProps)(MilestonesFeed);
